import { enGB, et, ru } from 'telia-front-react/locale';
import { observable, reaction, runInAction, computed } from 'mobx';
import { getAppFromUrl } from '../util/PathUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import CookieApi from '../util/CookieApi';

export type Language = 'et' | 'ru' | 'en';
export const LOCALE_COOKIE_NAME = 'LANGUAGE_EXT';
const SUPER_ALLOWED_LANGUAGES: string[] = ['et', 'ru', 'en'];
const DIIL_ALLOWED_LANGUAGES: string[] = ['et', 'ru'];

const DEFAULT_LANG: Language = 'et';

export const languageSlug = (lang: Language) => {
  return !lang || lang === DEFAULT_LANG ? '' : `/${lang}`;
};

export const langForCookieLocale = (locale: string): Language => {
  switch (locale) {
    case 'et_EE':
      return 'et';
    case 'ru_RU':
      return 'ru';
    case 'en_GB':
      return 'en';
    default:
      return null;
  }
};

export const cookieLocaleForLang = (lang: Language): string => {
  switch (lang) {
    case 'et':
      return 'et_EE';
    case 'ru':
      return 'ru_RU';
    case 'en':
      return 'en_GB';
    default:
      return '';
  }
};

export const getAllowedLanguages = (): string[] => {
  const app = getAppFromUrl(window.location.toString());
  return app === 'super' ? SUPER_ALLOWED_LANGUAGES : DIIL_ALLOWED_LANGUAGES;
};

class LanguageStore {
  @observable accessor currentLanguage: Language;

  private routeStore: RouteStore;
  private rootStore: RootStore;

  public constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.routeStore = rootStore.routeStore;
    runInAction(() => (this.currentLanguage = rootStore.initialState.currentLang || this.routeStore.langFromPath() || DEFAULT_LANG));
    reaction(
      () => this.currentLanguage,
      () => this.updateLangInPath()
    );
    window.onpopstate = () => this.updateLangInPath();
  }

  @computed
  get initialLanguageChanged() {
    return this.currentLanguage !== this.rootStore.initialState.currentLang;
  }

  get currentLocale() {
    const currentLanguage = this.currentLanguage;
    switch (currentLanguage) {
      case 'en':
        return enGB;
      case 'ru':
        return ru;
      case 'en':
        return enGB;
      default:
        return et;
    }
  }

  private updateLangInPath() {
    runInAction(() => this.routeStore.updateLangInPath(this.currentLanguage, DEFAULT_LANG));
  }

  public pathForCurrentLang(url: string) {
    if (url.startsWith('https')) {
      return url;
    }
    const lang = this.currentLanguage;
    return (lang !== DEFAULT_LANG && !this.urlIncludesAnyLangOrJavascript(url) ? `/${lang}` : '') + url;
  }

  private urlIncludesAnyLangOrJavascript(url: string) {
    return url && (url.startsWith('/et/') || url.startsWith('/ru/') || url.startsWith('/en/') || url.startsWith('javascript:'));
  }

  private updateCookiebotCulture() {
    const cookiebotScript = document.querySelector<HTMLScriptElement>('#Cookiebot');
    if (!cookiebotScript) {
      return;
    }

    cookiebotScript.dataset.culture = this.currentLanguage;
  }

  public setLanguage(locale: Language) {
    runInAction(() => (this.currentLanguage = locale));
    document.querySelector('html').setAttribute('lang', locale);
    CookieApi.updateCookie(LOCALE_COOKIE_NAME, cookieLocaleForLang(this.currentLanguage));
    this.updateCookiebotCulture();
  }
}

export default LanguageStore;
